import React, { useRef } from 'react'
import classNames from 'classnames'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { GuildAccessNotch } from '@/molecules/GuildAccessNotch'
import { LinkViewModel } from '@/services/RenderService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'

interface NumberedPosterLinkProps {
  link: LinkViewModel
  className?: string
  isGuildMember?: boolean
  rank?: number
  loading?: boolean
}

export function NumberedPosterLink(props: NumberedPosterLinkProps) {
  const track = useSafeTrack()
  const cardRef = useRef<HTMLDivElement>(null)
  const { isGuildMember = false, link, rank, loading } = props
  const isDoubleDigits = String(rank).length === 2

  const handleClick = React.useCallback(() => {
    if (link.track) track(link.track?.eventName, link.track?.payload)
  }, [link.track, track])

  if (!link.imageUrl) return null

  return (
    <div
      ref={cardRef}
      onClick={handleClick}
      className={classNames(
        'relative flex items-center space-x-4 py-4 mr-4', // keeps number + poster together
        'pl-[30px] md:pl-[50px] lg:pl-[65px] 2xl:pl-[85px]',
      )}
    >
      {/* Rank Number */}
      {typeof rank === 'number' && (
        <span
          className={classNames(
            'absolute top-5 text-[80px] font-extrabold leading-none tracking-tighter md:text-[120px] lg:text-[140px] 2xl:text-[170px] left-0',
            {
              'text-[70px] md:text-[105px] lg:text-[119px] 2xl:text-[150px]': isDoubleDigits,
            },
          )}
          style={{
            background: 'linear-gradient(to bottom, #512D12, #905F37)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            WebkitTextStroke: '1px #A87245', // thinner border
          }}
        >
          {rank}
        </span>
      )}

      {/* Poster card */}
      <div className="relative z-40 rounded-2xl shadow-[-16px_4px_25px_0_#000]">
        <InternalLink
          href={link.linkUrl}
          aria-label={`project-tile-${link.name}`}
          className="relative block h-fit max-h-full transform-gpu overflow-hidden rounded-2xl transition-transform duration-300 ease-in-out [backface-visibility:hidden] group-hover/rail:will-change-transform hover:z-10 md:hover:scale-[1.03]"
        >
          <Image
            aria-label={link.alt}
            alt={link.alt}
            src={getCloudinaryImageUrl({
              path: link.imageUrl,
              transforms: 'w_294',
            })}
            height={490}
            width={294}
            className="rounded-2xl bg-black"
          />
          <GuildAccessNotch isInEarlyAccess={link.isInEarlyAccess} isGuildMember={isGuildMember} loading={loading} />
        </InternalLink>
      </div>
    </div>
  )
}
